import {useQuery} from '@tanstack/react-query'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useContentstackRMS} from '../../../context/contentstack'
import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react'
import {getEntriesByIds} from '../../../custom-api/api/contentstack/sdk/entry'

export const CONTENT_TYPE_RECIPE = 'cooking_recipe'
export const CONTENT_TYPE_INGREDIENT = 'ingredient'
export const CONTENT_TYPE_UNIT = 'unit'

export const useGetRecipeData = (recipe_id) => {
    const {locale} = useMultiSite()
    const {stack} = useContentstackRMS()
    const {getTokenWhenReady} = useAccessToken()
    const api = useCommerceApi()

    return useQuery({
        queryKey: ['recipeAllData', recipe_id, locale?.alias],
        queryFn: async () => {
            const fetchProducts = async (skus, token) => {
                if (!skus || skus.length === 0) return []
                try {
                    const response = await api.shopperProducts.getProducts({
                        parameters: {
                            ids: skus.join(','),
                        },
                        expand: ['availability', 'images'],
                        headers: {Authorization: `Bearer ${token}`},
                    })
                    return response?.data || []
                } catch (e) {
                    console.error('Error getting products for related to recipe', e)
                    // Return empty on error to avoid breaking the whole page
                    return []
                }
            }

            // 1. Fetch Recipe
            const recipeData = await getEntriesByIds({
                stack,
                contentTypeUid: CONTENT_TYPE_RECIPE,
                locale: locale?.alias,
                uids: [recipe_id],
            })
            const recipe = recipeData?.[0] ?? null

            if (!recipe) {
                return {
                    recipe: null,
                    allIngredientsData: [],
                    allUnitsData: [],
                    productsUsedInRecipe: [],
                    toolsUsedInRecipe: [],
                }
            }

            // 2. Get IDs from recipe
            const ingredientIds = [
                ...new Set(
                    recipe.ingredients?.flatMap((ing) =>
                        ing?.ingredient?.map((i) => i.mainIngredient?.ingredientId),
                    ) || [],
                ),
            ].filter(Boolean)

            const unitIds = [
                ...new Set(
                    recipe.ingredients?.flatMap((ing) =>
                        ing?.ingredient?.map((i) => i.mainIngredient?.unitId),
                    ) || [],
                ),
            ].filter(Boolean)

            const recipeProductsSKUs =
                recipe.products_utensils?.product_model?.map((p) => p?.title) || []
            const recipeToolsSKUs = recipe.products_utensils?.tools?.map((p) => p?.title) || []

            // 3. Fetch dependent data in parallel
            const token = await getTokenWhenReady()
            const [allIngredientsData, allUnitsData, productsUsedInRecipe, toolsUsedInRecipe] =
                await Promise.all([
                    ingredientIds.length > 0
                        ? getEntriesByIds({
                              stack,
                              contentTypeUid: CONTENT_TYPE_INGREDIENT,
                              locale: locale?.alias,
                              uids: ingredientIds,
                          })
                        : [],
                    unitIds.length > 0
                        ? getEntriesByIds({
                              stack,
                              contentTypeUid: CONTENT_TYPE_UNIT,
                              locale: locale?.alias,
                              uids: unitIds,
                          })
                        : [],
                    fetchProducts(recipeProductsSKUs, token),
                    fetchProducts(recipeToolsSKUs, token),
                ])

            // 4. Return all data
            return {
                recipe,
                allIngredientsData,
                allUnitsData,
                productsUsedInRecipe,
                toolsUsedInRecipe,
            }
        },
        enabled: !!recipe_id,
    })
}
